import { FC } from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Typography } from 'src/components-v2/DataDisplay';
import { Stack } from 'src/components-v2/Layout';
import styled from 'threads5/styles/styled';

const StyledStack = styled(Stack)(({ theme }) => {
  return {
    justifyContent: 'center',
    overflowX: 'auto',
    margin: '0 40px',
    marginBottom: 122,
    [theme.breakpoints.down('md')]: {
      // padding needed to not crop the button dropshadow
      paddingLeft: 5,
      paddingRight: 5,
      marginLeft: 35,
      marginBottom: 45,
      paddingBottom: 15,
      justifyContent: 'start',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 19,
      marginRight: 0,
    },
  };
});

const Nav = styled(Stack)(({ theme }) => {
  return {
    // backgroundColor: '#F7FAFD',
    background: theme.palette['base/black/100'],
    mixBlendMode: 'multiply',
    padding: '8px',
    boxShadow: '0px 1.17px 0px 0px #FFFFFF, 0px 0px 1.55px 0px #8B8BF980 inset',
    borderRadius: 999,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      padding: 0,
      background: 'none',
      boxShadow: 'none',
      justifyContent: 'start',
      mixBlendMode: 'initial',
    },
    ul: {
      display: 'flex',
      gap: '2.4rem',
      [theme.breakpoints.down('lg')]: {
        gap: '1.2rem',
      },
      [theme.breakpoints.down('md')]: {
        gap: '0.8rem',
      },
      margin: 0,
    },
    li: {
      listStyle: 'none',
    },
  };
});

const Button = styled('button', {
  shouldForwardProp: (prop: string) => {
    return prop !== 'isActive';
  },
})<{
  isActive?: boolean;
}>(({ theme, isActive }) => {
  return {
    padding: '12px 24px',
    borderRadius: 999,
    // color: '#485C75',
    color: theme.palette['text/contrast-medium'],

    transition:
      'color 300ms ease-out, background-color 300ms ease-out, box-shadow 300ms ease-out',
    '&:hover': {
      color: '#051221',
      // TODO: swap for component/button/neutral/background-empty-interacted
      backgroundColor: '#6B778514',
    },
    ...(isActive && {
      // color: '#051221',
      color: theme.palette['text/contrast-max'],
      boxShadow: '0px 8px 8px 0px #01166A12',
      backgroundColor: theme.palette['base/white'],
      borderColor: 'transparent',
      '&:hover': {
        backgroundColor: theme.palette['base/white'],
      },
    }),
    [theme.breakpoints.down('lg')]: {
      padding: '10px 20px',
    },
    [theme.breakpoints.down('md')]: {
      // TODO: swap for border/border-primary
      border: '0.5px solid #C5C5C5',
      padding: '8px 16px',
      ...(isActive && {
        borderColor: 'transparent',
      }),
    },
  };
});

interface Tab {
  fields?: {
    title?: string;
  };
  sys?: {
    id?: string;
  };
}

interface Props {
  tabs?: Tab[];
  selectedIndex: number;
  onChange: (index: number) => void;
}

const TabButton: FC<{
  tab: Tab;
  isActive: boolean;
  onClick: () => void;
}> = ({ tab, isActive, onClick }) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: tab?.sys?.id,
  });

  return (
    <Button
      isActive={isActive}
      onClick={onClick}
      {...inspectorProps({ fieldId: 'title' })}
    >
      <Typography variant='P2 SemiBold' theme='web 3.0' component='span'>
        {tab?.fields?.title}
      </Typography>
    </Button>
  );
};

const TabbedNav: FC<Props> = ({ tabs, selectedIndex, onChange }) => {
  return (
    <StyledStack direction='row'>
      <Nav direction='row'>
        <ul>
          {tabs?.map((tab, i) => {
            return (
              <li key={i}>
                <TabButton
                  tab={tab}
                  isActive={selectedIndex === i}
                  onClick={() => {
                    return onChange(i);
                  }}
                />
              </li>
            );
          })}
        </ul>
      </Nav>
    </StyledStack>
  );
};

export default TabbedNav;
